/**
 * SnapClip API 客户端
 *
 * 提供与后端 API 的交互接口，支持内容的增删改查操作
 */

import { usePerformanceStore } from '../stores/performance';

// 根据环境自动选择 API 地址
// 生产环境使用相对路径，因为 Worker 通过 Cloudflare Routes 监听在同域名下
const API_BASE = import.meta.env.PROD
  ? '/api'
  : 'http://localhost:8789/api';

/**
 * 认证状态接口
 */
export interface AuthState {
  /** 是否已认证 */
  authenticated: boolean;
  /** 用户信息 */
  user: {
    id: string;
    email: string;
  } | null;
}

/**
 * 内容项接口
 */
export interface Item {
  /** 项目ID */
  id: number;
  /** 项目内容 */
  content: string;
  /** 关联的URL */
  url?: string;
  /** 来源 */
  source?: string;
  /** 备注 */
  notes?: string;
  /** 标签列表 */
  tags?: string[];
  /** 优先级 */
  priority?: 'low' | 'medium' | 'high';
  /** 截止日期（ISO 8601 格式） */
  due_date?: string;
  /** URL元数据 - 页面标题 */
  url_title?: string;
  /** URL元数据 - 页面描述 */
  url_description?: string;
  /** URL元数据 - 网站图标 */
  url_favicon?: string;
  /** URL元数据 - 网站名称 */
  url_site_name?: string;
  /** URL元数据 - 预览图片 */
  url_image?: string;
  /** 是否已完成 */
  is_completed: boolean;
  /** 是否已归档 */
  is_archived: boolean;
  /** 是否标记为今日专注 */
  is_focus: boolean;
  /** 创建时间 */
  created_at: string;
  /** 更新时间 */
  updated_at: string;
}

/**
 * 创建项目输入接口
 */
export interface CreateItemInput {
  /** 项目内容（必填） */
  content: string;
  /** 关联的URL */
  url?: string;
  /** 来源 */
  source?: string;
  /** 备注 */
  notes?: string;
  /** 标签列表 */
  tags?: string[];
  /** 优先级 */
  priority?: 'low' | 'medium' | 'high';
  /** 截止日期（ISO 8601 格式） */
  due_date?: string;
  /** 是否标记为今日专注 */
  is_focus?: boolean;
  /** URL元数据 - 页面标题 */
  url_title?: string;
  /** URL元数据 - 页面描述 */
  url_description?: string;
  /** URL元数据 - 网站图标 */
  url_favicon?: string;
  /** URL元数据 - 网站名称 */
  url_site_name?: string;
  /** URL元数据 - 预览图片 */
  url_image?: string;
}

/**
 * 更新项目输入接口
 */
export interface UpdateItemInput {
  /** 项目内容 */
  content?: string;
  /** 关联的URL */
  url?: string;
  /** 来源 */
  source?: string;
  /** 备注 */
  notes?: string;
  /** 标签列表 */
  tags?: string[];
  /** 优先级 */
  priority?: 'low' | 'medium' | 'high';
  /** 截止日期（ISO 8601 格式） */
  due_date?: string;
  /** 是否标记为今日专注 */
  is_focus?: boolean;
  /** 是否已归档 */
  is_archived?: boolean;
  /** URL元数据 - 页面标题 */
  url_title?: string;
  /** URL元数据 - 页面描述 */
  url_description?: string;
  /** URL元数据 - 网站图标 */
  url_favicon?: string;
  /** URL元数据 - 网站名称 */
  url_site_name?: string;
  /** URL元数据 - 预览图片 */
  url_image?: string;
  /** 是否已完成 */
  is_completed?: boolean;
}

/**
 * API 客户端类
 * 处理所有与后端的 HTTP 请求
 */
class ApiClient {
  private baseUrl: string;

  /**
   * 构造函数
   * @param baseUrl - API 基础 URL
   */
  constructor(baseUrl: string = API_BASE) {
    this.baseUrl = baseUrl;
  }

  /**
   * 发送 HTTP 请求
   * @param endpoint - API 端点路径
   * @param options - 请求选项
   * @returns Promise<T> 响应数据
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const config: RequestInit = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    };

    const startTime = Date.now();

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      const duration = Date.now() - startTime;

      // 记录性能指标（仅在浏览器环境中）
      if (typeof window !== 'undefined') {
        try {
          const perfStore = usePerformanceStore();
          perfStore.recordApiCall(endpoint, duration, response.ok);
        } catch {
          // 忽略性能监控错误
        }
      }

      // 处理 401 未认证错误
      if (response.status === 401) {
        const authError = new Error('Authentication required') as any;
        authError.code = 'UNAUTHORIZED';
        authError.status = 401;
        throw authError;
      }

      if (!response.ok) {
        throw new Error(data.error || 'Request failed');
      }

      return data;
    } catch (error: any) {
      const duration = Date.now() - startTime;

      // 记录失败请求
      if (typeof window !== 'undefined') {
        try {
          const perfStore = usePerformanceStore();
          perfStore.recordApiCall(endpoint, duration, false);
        } catch {
          // 忽略性能监控错误
        }
      }

      // 处理网络错误
      if (error.message === 'Failed to fetch') {
        const networkError = new Error('Network error. Please check your connection.') as any;
        networkError.code = 'NETWORK_ERROR';
        throw networkError;
      }

      console.error('API request failed:', error);
      throw error;
    }
  }

  /**
   * 获取所有项目
   * @returns Promise 包含成功状态和项目列表的响应
   */
  async getAllItems(): Promise<{ success: boolean; data: Item[] }> {
    return this.request('/items');
  }

  /**
   * 获取待处理项目
   * @returns Promise 包含成功状态和待处理项目列表的响应
   */
  async getPendingItems(): Promise<{ success: boolean; data: Item[] }> {
    return this.request('/items/pending');
  }

  /**
   * 获取今日专注项目
   * @returns Promise 包含成功状态和今日专注项目列表的响应
   */
  async getFocusItems(): Promise<{ success: boolean; data: Item[] }> {
    return this.request('/items/focus');
  }

  /**
   * 获取归档项目
   * @returns Promise 包含成功状态和归档项目列表的响应
   */
  async getArchivedItems(): Promise<{ success: boolean; data: Item[] }> {
    return this.request('/items/archived');
  }

  /**
   * 获取单个项目
   * @param id - 项目ID
   * @returns Promise 包含成功状态和项目详情的响应
   */
  async getItem(id: number): Promise<{ success: boolean; data: Item }> {
    return this.request(`/items/${id}`);
  }

  /**
   * 创建新项目
   * @param input - 创建项目输入
   * @returns Promise 包含成功状态和创建项目的响应
   */
  async createItem(input: CreateItemInput): Promise<{ success: boolean; data: Item }> {
    return this.request('/items', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  /**
   * 更新项目
   * @param id - 项目ID
   * @param input - 更新项目输入
   * @returns Promise 包含成功状态和更新后项目的响应
   */
  async updateItem(
    id: number,
    input: UpdateItemInput
  ): Promise<{ success: boolean; data: Item }> {
    return this.request(`/items/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  /**
   * 切换项目完成状态
   * @param id - 项目ID
   * @returns Promise 包含成功状态和更新后项目的响应
   */
  async toggleItem(id: number): Promise<{ success: boolean; data: Item }> {
    return this.request(`/items/${id}/toggle`, {
      method: 'PATCH',
    });
  }

  /**
   * 删除项目
   * @param id - 项目ID
   * @returns Promise 包含成功状态和消息的响应
   */
  async deleteItem(id: number): Promise<{ success: boolean; message: string }> {
    return this.request(`/items/${id}`, {
      method: 'DELETE',
    });
  }

  /**
   * 清除已完成的项目
   * @returns Promise 包含成功状态和消息的响应
   */
  async clearCompleted(): Promise<{ success: boolean; message: string }> {
    return this.request('/items/completed', {
      method: 'DELETE',
    });
  }

  /**
   * 预览 URL 元数据
   * @param url - 要预览的 URL
   * @returns Promise 包含成功状态和元数据的响应
   */
  async previewUrl(url: string): Promise<{ success: boolean; data: URLMetadata }> {
    const encodedUrl = encodeURIComponent(url);
    return this.request(`/preview?url=${encodedUrl}`);
  }

  /**
   * 获取当前认证状态
   * @returns Promise 认证状态
   */
  async getAuthState(): Promise<AuthState> {
    try {
      const response = await fetch(`${this.baseUrl}/auth/me`);
      const data = await response.json();

      if (response.ok) {
        return {
          authenticated: data.authenticated,
          user: data.user
        };
      }

      return {
        authenticated: false,
        user: null
      };
    } catch {
      return {
        authenticated: false,
        user: null
      };
    }
  }

  /**
   * 获取当前用户详细资料
   * @returns Promise 用户资料
   */
  async getCurrentUser(): Promise<{ success: boolean; data: UserProfile }> {
    return this.request('/users/me');
  }

  /**
   * 更新用户资料
   * @param input - 更新输入
   * @returns Promise 更新后的用户资料
   */
  async updateProfile(input: UpdateProfileInput): Promise<{ success: boolean; data: UserProfile }> {
    return this.request('/users/me', {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  /**
   * 登出
   * @returns Promise 登出URL
   */
  async logout(): Promise<{ success: boolean; logoutUrl: string; message: string }> {
    return this.request('/users/logout', {
      method: 'POST',
    });
  }
}

export const api = new ApiClient();

/**
 * URL 元数据接口
 */
export interface URLMetadata {
  /** 网页标题 */
  title: string;
  /** 网页描述 */
  description: string;
  /** Favicon URL */
  favicon: string;
  /** 网站名称 */
  siteName: string;
  /** Open Graph 类型 */
  ogType: string;
  /** 作者 */
  author: string;
  /** 原始 URL */
  url: string;
}

/**
 * 用户资料接口
 */
export interface UserProfile {
  /** 用户ID（邮箱） */
  id: string;
  /** 用户邮箱 */
  email: string;
  /** 用户昵称 */
  nickname?: string;
  /** 头像URL */
  avatar_url?: string;
  /** 创建时间 */
  created_at: string;
  /** 更新时间 */
  updated_at: string;
}

/**
 * 更新用户资料输入接口
 */
export interface UpdateProfileInput {
  /** 用户昵称（可选） */
  nickname?: string;
  /** 头像URL（可选） */
  avatar_url?: string | null;
}

/**
 * 登出响应接口
 */
export interface LogoutResponse {
  /** 登出URL */
  logoutUrl: string;
  /** 消息 */
  message: string;
}
